import { Condition, Operation } from '@ydistri/api-sdk';
import { createDebugLog, formatInFilterValue } from '@ydistri/utils';
import { NO_VALUE } from '../../../../../../../lib/utils/utilsTypes';

const debugLog = createDebugLog('skuRedistributionConditions');

export const SOURCE_SKU_CLASS_FIELD = 'SourceSku/SkuClassId';
export const TARGET_SKU_CLASS_FIELD = 'TargetSku/SkuClassId';
export const BRAND_FIELD = 'Brand/Id';
export const OUT_OF_STOCK_FIELD = 'TotalAvailableSupplyOfTargetSku';
export const FORCED_FIELD = 'IsSourceForced';

const removeCondition = (conditions: Condition[], fieldName: string): void => {
  const conditionIndex = conditions.findIndex(condition => condition.fieldName === fieldName);
  if (conditionIndex !== -1) {
    debugLog('field %s is present in conditions, removing it', fieldName);
    conditions.splice(conditionIndex, 1);
    debugLog('new conditions: %s', JSON.stringify(conditions));
  }
};

export interface RedistributionConditionFilters {
  brandId?: number;
  sourceSkuClassIds: number[];
  targetSkuClassIds: number[];
  outOfStock: boolean;
  forced: boolean;
}

/**
 * Builds the redistribution pairing filter conditions from the current filter selection.
 *
 * The returned conditions are combined by the axios interceptor with a logical AND (the default),
 * so Brand, SkuClass, out-of-stock and forced filters narrow the result set together. Each
 * SkuClass filter emits a single {@link Operation.In} condition (never one condition per id), with
 * the value formatted through {@link formatInFilterValue} to guarantee the mandatory `(1, 4)`
 * spacing. Any previously applied condition for a managed field is removed before a new one is
 * added, so toggling a filter off drops its condition.
 *
 * @param previousConditions conditions currently applied to the request (may be undefined)
 * @param filters the active redistribution filter selection
 * @returns the new list of conditions (empty when no filter is active)
 */
export const buildRedistributionConditions = (
  previousConditions: Condition[] | undefined,
  filters: RedistributionConditionFilters,
): Condition[] => {
  const conditions: Condition[] = previousConditions ? Array.from(previousConditions) : [];

  removeCondition(conditions, BRAND_FIELD);
  if (filters.brandId !== undefined && filters.brandId !== NO_VALUE) {
    conditions.push({
      fieldName: BRAND_FIELD,
      operation: Operation.Eq,
      value: filters.brandId,
    });
  }

  removeCondition(conditions, SOURCE_SKU_CLASS_FIELD);
  if (filters.sourceSkuClassIds.length > 0) {
    conditions.push({
      fieldName: SOURCE_SKU_CLASS_FIELD,
      operation: Operation.In,
      value: formatInFilterValue(filters.sourceSkuClassIds),
    });
  }

  removeCondition(conditions, TARGET_SKU_CLASS_FIELD);
  if (filters.targetSkuClassIds.length > 0) {
    conditions.push({
      fieldName: TARGET_SKU_CLASS_FIELD,
      operation: Operation.In,
      value: formatInFilterValue(filters.targetSkuClassIds),
    });
  }

  removeCondition(conditions, OUT_OF_STOCK_FIELD);
  if (filters.outOfStock) {
    conditions.push({
      fieldName: OUT_OF_STOCK_FIELD,
      operation: Operation.Eq,
      value: 0,
    });
  }

  removeCondition(conditions, FORCED_FIELD);
  if (filters.forced) {
    conditions.push({
      fieldName: FORCED_FIELD,
      operation: Operation.Eq,
      value: true,
    });
  }

  return conditions;
};
